import { videoReplicationsApi, type VideoReplicationReferenceRole, type GenjutsuMode, type VideoReplicationModel } from '@/lib/api/video-replications';
import type { UserMaterial } from '@/lib/api/materials';
import type { KagemaneTemplate } from '@/lib/kagemane-catalog';
import type { KagemaneResolution } from '@/components/ui/KagemaneResolutionChoice';

export type KagemaneSession = { projectId?: string;
  pendingRequest?: { fingerprint: string; id: string; dispatched?: boolean; genjutsuMaxCredits?: number; giftMaxCredits?: number } };
const defaultDependencies = { videoReplicationsApi };

/** Library references and owned uploads share one generation admission. */
export interface KagemaneGenerationInput {
  session: KagemaneSession;
  template: KagemaneTemplate | null;
  customReference: UserMaterial | null;
  materials: { material: UserMaterial; role: VideoReplicationReferenceRole }[];
  resolution: KagemaneResolution;
  model?: 'seedance' | 'heygen' | 'kling4' | 'genjutsu';
  genjutsuMode?: GenjutsuMode;
  genjutsuPresetId?: string;
  genjutsuMaxCredits?: number;
  useMembershipGift?: boolean;
  giftMaxCredits?: number;
  depthEnabled?: boolean;
  prompt: string;
  name: string;
  origin: string;
  errorMessage: string;
  assertOwner?: () => void;
}

export async function submitKagemaneGeneration(input: KagemaneGenerationInput, deps = defaultDependencies) {
  const assertOwner = input.assertOwner ?? (() => {});
  assertOwner();
  // Capture one click's inputs before task admission yields to the UI.
  input = { ...input, template: input.template ? { ...input.template } : null,
    customReference: input.customReference ? { ...input.customReference } : null,
    materials: input.materials.map(({ material, role }) => ({ material: { ...material }, role })),
  };
  const { session, template, customReference, materials } = input;
  if (!customReference && !template) throw new Error(input.errorMessage);
  const longReference = (customReference ? customReference.duration ?? 0 : template!.durationSeconds) > 15;
  const heygen = input.model === 'heygen';
  const kling4 = input.model === undefined || input.model === 'kling4';
  const genjutsu = input.model === 'genjutsu';
  if (!genjutsu && input.prompt.length > 6000) throw new Error(input.errorMessage);
  const mode = input.genjutsuMode ?? 'motion-transfer';
  const videoModel: VideoReplicationModel = genjutsu ? `higgsfield/genjutsu/${mode}/v1.0` : kling4 ? 'kling-v4-omni' : heygen ? 'heygen-video-1' : longReference ? 'seedance-2.5' : 'seedance-2.0';
  if (heygen && (longReference || materials.length > 9 || !['480p', '768p'].includes(input.resolution))) throw new Error(input.errorMessage);
  if (kling4 && (materials.length > 10 || !['720p', '1080p', '2k', '4k'].includes(input.resolution))) throw new Error(input.errorMessage);
  const resolution = input.resolution;
  const duration = customReference ? customReference.duration : template!.durationSeconds;
  const depthEnabled = !genjutsu && input.depthEnabled === true;
  const genjutsuPresetId = genjutsu && mode === 'restyle' ? input.genjutsuPresetId : undefined;
  const genjutsuMaxCredits = genjutsu ? input.genjutsuMaxCredits : undefined;
  if (genjutsu && (!['motion-transfer', 'object-swap', 'restyle'].includes(mode)
    || typeof duration !== 'number' || !Number.isFinite(duration) || duration < 4 || duration > 30
    || !['480p', '720p', '1080p'].includes(resolution) || input.prompt.length > 10000
    || (genjutsuMaxCredits !== undefined && (!Number.isSafeInteger(genjutsuMaxCredits) || genjutsuMaxCredits < 1))
    || materials.length > (mode === 'restyle' ? 5 : 8)
    || (mode === 'restyle' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(genjutsuPresetId ?? ''))
    || (mode === 'object-swap' && customReference?.width && customReference?.height && customReference.width * customReference.height < 409600))) throw new Error(input.errorMessage);
  if (input.useMembershipGift && (!['480p', '720p', '768p', '1080p'].includes(resolution)
    || typeof duration !== 'number' || !Number.isFinite(duration) || duration < 4 || duration > 30
    || (duration > 15 && input.giftMaxCredits === undefined)
    || (input.giftMaxCredits !== undefined && (!Number.isSafeInteger(input.giftMaxCredits) || input.giftMaxCredits < 0)))) {
    throw new Error(input.errorMessage);
  }
  if ((!materials.length && !(genjutsu && mode === 'restyle')) || materials.some(({ material }) => !material.id)) throw new Error(input.errorMessage);
  if (customReference && !customReference.url) throw new Error(input.errorMessage);
  const fingerprint = JSON.stringify({
    projectId: session.projectId,
    source: customReference ? { id: customReference.id, url: customReference.url, duration } : { catalogId: template!.id },
    materials: materials.map(({ material, role }) => ({ id: material.id, url: material.url, role })),
    ...(depthEnabled ? { depthEnabled: true } : {}),
    videoModel, genjutsuPresetId, genjutsuMaxCredits, resolution, prompt: input.prompt, gift: input.useMembershipGift === true,
    ...(input.useMembershipGift && input.giftMaxCredits !== undefined ? {giftMaxCredits: input.giftMaxCredits} : {}),
  });
  if (session.pendingRequest?.dispatched && session.pendingRequest.fingerprint !== fingerprint) {
    throw new Error(input.errorMessage);
  }
  if (session.pendingRequest?.fingerprint !== fingerprint) {
    session.pendingRequest = { fingerprint, id: crypto.randomUUID(), ...(genjutsuMaxCredits !== undefined ? { genjutsuMaxCredits } : {}),
      ...(input.useMembershipGift && input.giftMaxCredits !== undefined ? {giftMaxCredits: input.giftMaxCredits} : {}) };
  }
  const clientRequestId = session.pendingRequest.id;
  const billingMode = input.useMembershipGift ? 'monthly_gift' as const : 'dobi' as const;
  // Only an explicitly opened existing project supplies context. Independent
  // generations belong to the account and must never consume project quota.
  const projectId = session.projectId;
  assertOwner();
  session.pendingRequest.dispatched = true;
  const response = await deps.videoReplicationsApi.create({
    entry_point: 'kagemane', video_model: videoModel, depth_enabled: depthEnabled,
    ...(genjutsuPresetId ? { genjutsu_preset_id: genjutsuPresetId } : {}),
    ...(genjutsuMaxCredits !== undefined ? { genjutsu_max_credits: genjutsuMaxCredits } : {}),
    client_request_id: clientRequestId, billing_mode: billingMode,
    ...(input.useMembershipGift && input.giftMaxCredits !== undefined ? {gift_max_credits: input.giftMaxCredits} : {}),
    project_id: projectId,
    ...(customReference ? { source_asset_id: customReference.id, source_video_url: customReference.url! }
      : { source_catalog_id: template!.id }),
    source_duration_ms: duration ? Math.round(duration * 1000) : undefined,
    reference_image_urls: materials.map(({ material }) => material.url || material.thumbnail_url || ''),
    reference_asset_ids: materials.map(({ material }) => material.id),
    reference_roles: materials.map(({ role }) => role),
    instruction: input.prompt, resolution,
  });
  assertOwner();
  if (!response.success || !response.data?.task_id || (projectId && !response.data.canvas_node_id?.trim())) {
    if ((response.error?.httpStatus && response.error.httpStatus >= 400 && response.error.httpStatus < 500 && response.error.httpStatus !== 408
      && !response.error.details?.operation_id && response.error.code !== 'media_command_pending')
      || ['KAGEMANE_DEPTH_UNAVAILABLE', 'KAGEMANE_DEPTH_UNSUPPORTED', 'KAGEMANE_GIFT_UNAVAILABLE', 'KAGEMANE_GENJUTSU_UNAVAILABLE', 'KAGEMANE_GENJUTSU_QUOTE_CHANGED', 'HYPIT_REQUEST_CONFLICT', 'VALIDATION_ERROR', 'INSUFFICIENT_CREDITS', 'INVALID_INPUT', 'FORBIDDEN'].includes(response.error?.code ?? '')) {
      session.pendingRequest = undefined;
    }
    throw response.error ?? new Error(input.errorMessage);
  }
  session.pendingRequest = undefined;
  return { ...response.data, project_id: projectId };
}
